import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiBarChart2, FiCheckCircle, FiClock, FiCopy, FiInbox, FiLink, FiRefreshCw, FiTrash2, FiUsers } from 'react-icons/fi';
import BASE_URL from '../../../../config/config';
import FilaAjuste from '../../../../components/ui/FilaAjuste';
import { cabecerasAuth } from '../../../../queries/cliente';
import '../../../../css/integracion-informe.css';

const formatoFecha = (valor, idioma) => (valor
  ? new Date(valor).toLocaleString(idioma, { dateStyle: 'medium', timeStyle: 'short' })
  : null);

/**
 * Conexión de solo lectura con Adhoc Manager para el informe web mensual del
 * cliente. Solo administradores: el token da acceso a las cifras de tickets de
 * la cuenta, así que se trata como cualquier otra credencial.
 */
const PanelIntegracionInforme = ({ usuario, onAviso: avisar }) => {
  const { t, i18n } = useTranslation();
  // En una ref: el padre pasa una función nueva en cada render y, como
  // dependencia de `cargar`, relanzaría la petición sin fin.
  const avisoRef = useRef(avisar);
  avisoRef.current = avisar;
  const onAviso = (tipo, mensaje) => avisoRef.current?.(tipo, mensaje);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState('');
  const [tokenNuevo, setTokenNuevo] = useState(null);
  const [confirmar, setConfirmar] = useState('');
  const [copiado, setCopiado] = useState('');
  const [vinculadosPendiente, setVinculadosPendiente] = useState(false);

  const ruta = `${BASE_URL}/api/admin/usuarios/${encodeURIComponent(usuario)}/integracion-informe`;

  const cargar = useCallback(async () => {
    try {
      const respuesta = await axios.get(ruta, cabecerasAuth());
      setDatos(respuesta.data);
    } catch (error) {
      onAviso?.('error', error.response?.data?.message || t('integracionInforme.errorCargar'));
    } finally {
      setCargando(false);
    }
  }, [ruta, t]);

  useEffect(() => { cargar(); }, [cargar]);

  const copiar = async (texto, clave) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(clave);
      setTimeout(() => setCopiado(''), 2000);
    } catch (error) {
      onAviso?.('error', t('integracionInforme.errorCopiar'));
    }
  };

  const generar = async () => {
    setTrabajando('generar');
    setConfirmar('');
    try {
      const respuesta = await axios.post(ruta, { incluir_vinculados: vinculadosPendiente }, cabecerasAuth());
      setTokenNuevo(respuesta.data.token);
      onAviso?.('success', respuesta.data.regenerada ? t('integracionInforme.regenerada') : t('integracionInforme.generada'));
      await cargar();
    } catch (error) {
      onAviso?.('error', error.response?.data?.message || t('integracionInforme.errorGenerar'));
    } finally {
      setTrabajando('');
    }
  };

  const revocar = async () => {
    setTrabajando('revocar');
    setConfirmar('');
    try {
      await axios.delete(ruta, cabecerasAuth());
      setTokenNuevo(null);
      onAviso?.('success', t('integracionInforme.revocada'));
      await cargar();
    } catch (error) {
      onAviso?.('error', error.response?.data?.message || t('integracionInforme.errorRevocar'));
    } finally {
      setTrabajando('');
    }
  };

  const cambiarVinculados = async (valor) => {
    if (!datos?.conexion) {
      setVinculadosPendiente(valor);
      return;
    }
    setTrabajando('vinculados');
    try {
      await axios.patch(ruta, { incluir_vinculados: valor }, cabecerasAuth());
      await cargar();
    } catch (error) {
      onAviso?.('error', error.response?.data?.message || t('integracionInforme.errorGuardar'));
    } finally {
      setTrabajando('');
    }
  };

  if (cargando) return <div className="ii-cargando">{t('integracionInforme.cargando')}</div>;
  if (!datos) return null;

  const { conexion, vinculados, vista_previa: previa } = datos;
  const resumen = previa?.resumen || {};
  const incluirVinculados = conexion ? conexion.incluir_vinculados : vinculadosPendiente;
  const horas = resumen.tiempo_medio_resolucion_horas;
  const tiempoMedio = horas === null || horas === undefined
    ? '—'
    : horas < 48 ? t('integracionInforme.horas', { valor: horas }) : t('integracionInforme.dias', { valor: Math.round(horas / 24 * 10) / 10 });

  const kpis = [
    { clave: 'abiertos', icono: FiInbox, valor: resumen.abiertos ?? 0 },
    { clave: 'resueltos', icono: FiCheckCircle, valor: resumen.resueltos ?? 0 },
    { clave: 'pendientes', icono: FiClock, valor: resumen.pendientes ?? 0 },
    { clave: 'tiempoMedio', icono: FiRefreshCw, valor: tiempoMedio },
  ];

  return (
    <div className="ii-panel">
      <div className={`ii-cabecera ${conexion ? 'ii-cabecera--activa' : ''}`}>
        <span className="ii-cabecera-icono"><FiBarChart2 /></span>
        <div>
          <h3>{t('integracionInforme.titulo')}</h3>
          <p>{t('integracionInforme.descripcion')}</p>
        </div>
        <span className={`ii-estado ${conexion ? 'ii-estado--activa' : ''}`}>
          <span className="ii-estado-punto" />
          {conexion ? t('integracionInforme.conectada') : t('integracionInforme.sinConectar')}
        </span>
      </div>

      <FilaAjuste
        titulo={t('integracionInforme.vistaPrevia')}
        texto={t('integracionInforme.vistaPreviaTexto')}
        ancho
      >
        <div className="ii-kpis">
          {kpis.map(({ clave, icono: Icono, valor }) => (
            <div key={clave} className={`ii-kpi ii-kpi--${clave}`}>
              <span className="ii-kpi-icono"><Icono /></span>
              <strong>{valor}</strong>
              <span>{t(`integracionInforme.kpis.${clave}`)}</span>
            </div>
          ))}
        </div>
        {resumen.resueltos_aproximados > 0 && (
          <p className="ii-nota">{t('integracionInforme.aproximados', { count: resumen.resueltos_aproximados })}</p>
        )}
      </FilaAjuste>

      <FilaAjuste titulo={t('integracionInforme.conexion')} texto={t('integracionInforme.conexionTexto')} ancho>
        {conexion ? (
          <div className="ii-conexion">
            <div className="ii-conexion-datos">
              <code>{conexion.prefijo}…</code>
              <span>{t('integracionInforme.creadaPor', { usuario: conexion.creado_por, fecha: formatoFecha(conexion.createdAt, i18n.language) })}</span>
              <span>
                {conexion.ultimo_uso
                  ? t('integracionInforme.ultimoUso', { fecha: formatoFecha(conexion.ultimo_uso, i18n.language), count: conexion.consultas })
                  : t('integracionInforme.sinUsar')}
              </span>
            </div>
            {confirmar ? (
              <div className="ii-confirmar">
                <span>{confirmar === 'revocar' ? t('integracionInforme.confirmarRevocar') : t('integracionInforme.confirmarRegenerar')}</span>
                <button type="button" className="ii-boton ii-boton--secundario" onClick={() => setConfirmar('')}>{t('comun.cancelar')}</button>
                <button
                  type="button"
                  className={`ii-boton ${confirmar === 'revocar' ? 'ii-boton--peligro' : ''}`}
                  onClick={confirmar === 'revocar' ? revocar : generar}
                  disabled={Boolean(trabajando)}
                >
                  {confirmar === 'revocar' ? t('integracionInforme.revocar') : t('integracionInforme.regenerar')}
                </button>
              </div>
            ) : (
              <div className="ii-acciones">
                <button type="button" className="ii-boton ii-boton--secundario" onClick={() => setConfirmar('regenerar')} disabled={Boolean(trabajando)}>
                  <FiRefreshCw aria-hidden="true" /> {t('integracionInforme.regenerar')}
                </button>
                <button type="button" className="ii-boton ii-boton--peligro-suave" onClick={() => setConfirmar('revocar')} disabled={Boolean(trabajando)}>
                  <FiTrash2 aria-hidden="true" /> {t('integracionInforme.revocar')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button type="button" className="ii-boton" onClick={generar} disabled={Boolean(trabajando)}>
            <FiLink aria-hidden="true" /> {trabajando === 'generar' ? t('integracionInforme.generando') : t('integracionInforme.generar')}
          </button>
        )}

        {tokenNuevo && (
          <div className="ii-token" role="alert">
            <strong>{t('integracionInforme.tokenListo')}</strong>
            <p>{t('integracionInforme.tokenSoloUnaVez')}</p>
            <span className="ii-etiqueta">{t('integracionInforme.token')}</span>
            <div className="ii-copiable">
              <code>{tokenNuevo}</code>
              <button type="button" onClick={() => copiar(tokenNuevo, 'token')}>
                <FiCopy aria-hidden="true" /> {copiado === 'token' ? t('integracionInforme.copiado') : t('integracionInforme.copiar')}
              </button>
            </div>
            <span className="ii-etiqueta">{t('integracionInforme.urlApi')}</span>
            <div className="ii-copiable">
              <code>{BASE_URL}</code>
              <button type="button" onClick={() => copiar(BASE_URL, 'url')}>
                <FiCopy aria-hidden="true" /> {copiado === 'url' ? t('integracionInforme.copiado') : t('integracionInforme.copiar')}
              </button>
            </div>
            <p className="ii-pasos">{t('integracionInforme.pasos')}</p>
            <button type="button" className="ii-boton ii-boton--secundario" onClick={() => setTokenNuevo(null)}>{t('integracionInforme.hecho')}</button>
          </div>
        )}
      </FilaAjuste>

      {vinculados > 0 && (
        <FilaAjuste titulo={t('integracionInforme.vinculados')} texto={t('integracionInforme.vinculadosTexto', { count: vinculados })}>
          <label className="ii-interruptor">
            <input
              type="checkbox"
              checked={incluirVinculados}
              disabled={trabajando === 'vinculados'}
              onChange={(e) => cambiarVinculados(e.target.checked)}
            />
            <span className="ii-interruptor-pista" aria-hidden="true" />
            <span><FiUsers aria-hidden="true" /> {t('integracionInforme.incluirVinculados')}</span>
          </label>
        </FilaAjuste>
      )}
    </div>
  );
};

export default PanelIntegracionInforme;
